import OrganizationForm from '@app/Organization/OrganizationForm';
import * as React from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';

import { BuilderImageIcon } from '@patternfly/react-icons';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  EmptyState,
  EmptyStateBody,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import Unauthenticated from '@app/Unauthenticated';
import { useUserContext } from '../../contexts/user/UserContext';
import WalletTab from './Billing/WalletTab';
import WalletStatementTab from './Billing/WalletStatementTab';

type BillingTabKey = 'organization' | 'wallet' | 'statement';

const isBillingTabKey = (value: unknown): value is BillingTabKey =>
  value === 'organization' || value === 'wallet' || value === 'statement';

/**
 * Shown in the wallet/statement tabs for a user who has no organization yet (pre-onboarding): the
 * wallet endpoints require a client in scope, so nothing is fetched until one exists.
 */
const NoOrganizationYet: React.FunctionComponent = () => (
  <EmptyState status="info" titleText="Create your organization first" headingLevel="h2" variant="lg">
    <EmptyStateBody>
      The prepaid balance and saved cards belong to your organization. Create it in the Organization tab, then come back
      here to top up your balance or save a card.
    </EmptyStateBody>
  </EmptyState>
);

/**
 * Organization › Billing (/organization/billing). Three tabs: the organization profile form, the
 * prepaid balance + saved cards, and the wallet statement. `?tab=wallet|statement` preselects a tab
 * (the payment return page links back with it) and the param follows the active tab (replace, so
 * tab switches don't pile up in history). Tabs are mounted lazily (`mountOnEnter`) so the wallet
 * calls fire only when their tab is first opened.
 */
function Organization() {
  const auth = useAuth();
  const user = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasClient = (user?.clientID ?? null) !== null;

  const requestedTab = searchParams.get('tab');
  const activeTab: BillingTabKey = isBillingTabKey(requestedTab) ? requestedTab : 'organization';

  const selectTab = (key: BillingTabKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'organization') {
      next.delete('tab');
    } else {
      next.set('tab', key);
    }
    setSearchParams(next, { replace: true });
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization" isActive>
            Organization
          </BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <BuilderImageIcon />
          </Icon>
          Organization
        </Content>
        <Content component="p">
          Your organization&apos;s details, its prepaid balance and saved cards, and the statement of your balance.
        </Content>
      </PageSection>

      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => {
            if (isBillingTabKey(tabKey)) {
              selectTab(tabKey);
            }
          }}
          mountOnEnter
          aria-label="Organization and billing"
        >
          <Tab eventKey="organization" title={<TabTitleText>Organization</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <OrganizationForm />
            </div>
          </Tab>
          <Tab eventKey="wallet" title={<TabTitleText>Prepaid balance &amp; cards</TabTitleText>}>
            <div className="pf-v6-u-pt-md">{hasClient ? <WalletTab /> : <NoOrganizationYet />}</div>
          </Tab>
          <Tab eventKey="statement" title={<TabTitleText>Statement</TabTitleText>}>
            <div className="pf-v6-u-pt-md">{hasClient ? <WalletStatementTab /> : <NoOrganizationYet />}</div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Organization;
